import type { PixelBuffer } from '../core/filters';
import { blendModeToComposite, degreesToRadians } from '../core/model/compositing';
import { invertMaskPixels } from '../core/model/mask';
import type { LayerSource, RasterSource } from '../core/model/merge';
import type { BlendMode, Rect } from '../core/model/types';
import { textBox } from '../core/paint/text';
import type { PixelOp } from '../core/transforms';
import { loadImageUrl } from './imageIO';
import { drawTextLayer } from './textRaster';

/**
 * Executes the pixel work described by a `TransformPlan`.
 *
 * Every operation returns a fresh PNG data URL: PNG keeps alpha, which matters
 * because cropping and rotating a layer routinely exposes transparent edges.
 * The source is never mutated, so undo is just the previous document.
 */

/** Decode a layer (or mask) raster into raw pixels. */
export async function decodeLayer(
  src: string,
  width: number,
  height: number,
): Promise<PixelBuffer> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot render a canvas.');
  if (src) {
    const image = await loadImageUrl(src);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  }
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return { data: image.data, width: image.width, height: image.height };
}

/** Put raw pixels back into a PNG data URL. */
export function encodeLayer(buffer: PixelBuffer): string {
  const canvas = document.createElement('canvas');
  canvas.width = buffer.width;
  canvas.height = buffer.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot render a canvas.');
  const image = ctx.createImageData(buffer.width, buffer.height);
  image.data.set(buffer.data);
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}

function drawToDataUrl(
  width: number,
  height: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot render a canvas.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  draw(context);
  return canvas.toDataURL('image/png');
}

export function applyPixelOp(src: string, op: PixelOp): Promise<string> {
  return loadImageUrl(src).then((image) => {
    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;

    switch (op.kind) {
      case 'crop':
        return drawToDataUrl(op.width, op.height, (ctx) => {
          ctx.drawImage(
            image,
            op.sourceX,
            op.sourceY,
            Math.max(1, op.width),
            Math.max(1, op.height),
            0,
            0,
            Math.max(1, op.width),
            Math.max(1, op.height),
          );
        });

      case 'rotate': {
        const width = sourceHeight;
        const height = sourceWidth;
        return drawToDataUrl(width, height, (ctx) => {
          if (op.direction === 1) {
            ctx.translate(width, 0);
            ctx.rotate(Math.PI / 2);
          } else {
            ctx.translate(0, height);
            ctx.rotate(-Math.PI / 2);
          }
          ctx.drawImage(image, 0, 0);
        });
      }

      case 'flip':
        return drawToDataUrl(sourceWidth, sourceHeight, (ctx) => {
          if (op.axis === 'horizontal') {
            ctx.translate(sourceWidth, 0);
            ctx.scale(-1, 1);
          } else {
            ctx.translate(0, sourceHeight);
            ctx.scale(1, -1);
          }
          ctx.drawImage(image, 0, 0);
        });

      case 'resample':
        return drawToDataUrl(op.width, op.height, (ctx) => {
          ctx.drawImage(image, 0, 0, op.width, op.height);
        });
    }
  });
}

/** Read the intrinsic size of a data URL without decoding the whole document. */
export async function measureDataUrl(src: string): Promise<{ width: number; height: number }> {
  const image = await loadImageUrl(src);
  return { width: image.naturalWidth, height: image.naturalHeight };
}

/** A fully transparent layer the size of the canvas — what "New Layer" creates. */
export function blankLayerDataUrl(width: number, height: number): string {
  return drawToDataUrl(width, height, () => undefined);
}

/** A canvas-sized layer filled with one colour — what a fill layer becomes. */
export function fillLayerDataUrl(color: string, width: number, height: number): string {
  return drawToDataUrl(width, height, (ctx) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
  });
}

/** Collect every leaf under a source tree, for the preload pass. */
function collectLeaves(sources: RasterSource[], out: LayerSource[]): void {
  for (const source of sources) {
    if (source.kind === 'composite') collectLeaves(source.sources, out);
    else if (source.kind === 'clip') {
      collectLeaves([source.base], out);
      collectLeaves(source.clipped, out);
    } else out.push(source);
  }
}

/**
 * One leaf's pixels, drawn exactly the way the engine's live scene draws them:
 * centre-origin composition, rotation about the drawn centre, the mask going
 * on with `destination-in` on the source's own sheet.
 */
function drawLeafSource(
  ctx: CanvasRenderingContext2D,
  source: LayerSource,
  image: HTMLImageElement | null,
  mask: HTMLCanvasElement | null,
  region: Rect,
): void {
  // Text has no raster to decode: the shared renderer draws the glyphs
  // through the same centre-origin composition every other source uses.
  if (source.kind === 'text') {
    const layer = source.text;
    if (!layer) return;
    const box = textBox(layer);
    ctx.save();
    ctx.globalAlpha = source.opacity;
    ctx.translate(source.x - region.x + source.width / 2, source.y - region.y + source.height / 2);
    if (source.angle) ctx.rotate(degreesToRadians(source.angle));
    ctx.scale(layer.scaleX, layer.scaleY);
    ctx.translate(-box.width / 2, -box.height / 2);
    drawTextLayer(ctx, layer);
    ctx.restore();
    return;
  }

  const paint = (target: CanvasRenderingContext2D) => {
    target.imageSmoothingEnabled = true;
    target.imageSmoothingQuality = 'high';
    if (source.kind === 'fill') {
      // A fill layer covers the document and never rotates: draw it plainly.
      target.translate(source.x - region.x, source.y - region.y);
      target.fillStyle = source.color ?? '#ffffff';
      target.fillRect(0, 0, source.width, source.height);
      return;
    }
    // The same composition the engine's export path uses (`placeLayer`):
    // translate to the source's drawn centre, rotate about it, draw centred.
    // Rotating about the top-left instead made every merge or flatten of a
    // rotated layer disagree with the pixels it replaced (design decision 6).
    target.translate(
      source.x - region.x + source.width / 2,
      source.y - region.y + source.height / 2,
    );
    if (source.angle) target.rotate(degreesToRadians(source.angle));
    target.drawImage(
      image as CanvasImageSource,
      -source.width / 2,
      -source.height / 2,
      source.width,
      source.height,
    );
  };

  if (mask) {
    // A masked source is drawn on its own sheet first: `destination-in` erases
    // what is *under* it, and in a merge what is under it is the next layer.
    const sheet = makeCanvas(region.width, region.height);
    const sheetCtx = sheet.getContext('2d');
    if (!sheetCtx) throw new Error('This browser cannot render a canvas.');
    paint(sheetCtx);
    // The mask lives in the layer's own space, so it goes on with the same
    // transform the content had.
    sheetCtx.setTransform(1, 0, 0, 1, 0, 0);
    sheetCtx.globalCompositeOperation = 'destination-in';
    sheetCtx.drawImage(mask, 0, 0);
    ctx.save();
    ctx.globalAlpha = source.opacity;
    ctx.globalCompositeOperation = blendModeToComposite(
      source.blendMode,
    ) as GlobalCompositeOperation;
    ctx.drawImage(sheet, 0, 0);
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.globalAlpha = source.opacity;
  ctx.globalCompositeOperation = blendModeToComposite(source.blendMode) as GlobalCompositeOperation;
  paint(ctx);
  ctx.restore();
}

function makeCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

/** One source — leaf, composite buffer or clip run — onto a parent canvas. */
function drawSource(
  ctx: CanvasRenderingContext2D,
  source: RasterSource,
  region: Rect,
  images: Map<string, HTMLImageElement>,
  masks: Map<string, HTMLCanvasElement>,
): void {
  if (source.kind === 'composite') {
    // The children composite among themselves first, in their own buffer; the
    // buffer is then placed with the group's one opacity and blend mode. That
    // is the whole difference between a composite group and a folder.
    const buffer = makeCanvas(source.region.width, source.region.height);
    const bufferCtx = buffer.getContext('2d');
    if (!bufferCtx) throw new Error('This browser cannot render a canvas.');
    for (const child of source.sources) {
      drawSource(bufferCtx, child, source.region, images, masks);
    }
    ctx.save();
    ctx.globalAlpha = source.opacity;
    ctx.globalCompositeOperation = blendModeToComposite(
      source.blendMode,
    ) as GlobalCompositeOperation;
    ctx.drawImage(buffer, source.region.x - region.x, source.region.y - region.y);
    ctx.restore();
    return;
  }

  if (source.kind === 'clip') {
    // The run renders into one sheet: the base as it would appear on its own
    // (its opacity and mask are baked into its pixels), then each clipped
    // layer stencilled by that rendering and drawn with its own blend mode.
    // The sheet is placed with the base's blend mode.
    const sheet = makeCanvas(source.region.width, source.region.height);
    const sheetCtx = sheet.getContext('2d');
    if (!sheetCtx) throw new Error('This browser cannot render a canvas.');

    // The stencil: the base alone, rendered into its own canvas so its pixels
    // can be reused as coverage without compositing anything else.
    const coverage = makeCanvas(source.region.width, source.region.height);
    const coverageCtx = coverage.getContext('2d');
    if (!coverageCtx) throw new Error('This browser cannot render a canvas.');
    drawSource(coverageCtx, source.base, source.region, images, masks);

    const place = (canvas: HTMLCanvasElement, blendMode: BlendMode, alpha: number) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.globalCompositeOperation = blendModeToComposite(blendMode) as GlobalCompositeOperation;
      ctx.drawImage(canvas, source.region.x - region.x, source.region.y - region.y);
      ctx.restore();
    };

    drawSource(sheetCtx, source.base, source.region, images, masks);
    for (const member of source.clipped) {
      const stencilled = makeCanvas(source.region.width, source.region.height);
      const stencilledCtx = stencilled.getContext('2d');
      if (!stencilledCtx) throw new Error('This browser cannot render a canvas.');
      drawSource(stencilledCtx, member, source.region, images, masks);
      stencilledCtx.setTransform(1, 0, 0, 1, 0, 0);
      stencilledCtx.globalCompositeOperation = 'destination-in';
      stencilledCtx.drawImage(coverage, 0, 0);
      // A leaf member's opacity and blend mode were baked into its own
      // rendering; its blend mode goes on here, against the sheet's content —
      // the base and any members above it. A composite member already placed
      // its own buffer with its own opacity and blend, so it lands plainly.
      if (member.kind === 'composite' || member.kind === 'clip') {
        place(stencilled, 'normal', 1);
      } else {
        place(stencilled, member.blendMode, 1);
      }
    }
    // The sheet takes the base's blend mode — for a leaf base, whose blend
    // has not meaningfully applied yet (it drew over a transparent sheet); a
    // composite or clip base already applied its own.
    const baseBlend: BlendMode =
      source.base.kind === 'composite' || source.base.kind === 'clip'
        ? 'normal'
        : source.base.blendMode;
    place(sheet, baseBlend, 1);
    return;
  }

  const image = source.src ? (images.get(source.src) ?? null) : null;
  const mask = source.mask ? (masks.get(source.mask.src) ?? null) : null;
  drawLeafSource(ctx, source, image, mask, region);
}

/**
 * Draw a stack of sources into one region and return the result as a PNG.
 *
 * This is the pixel half of merging: `core/model/merge.ts` decided which
 * sources and which region, this reproduces the engine's compositing — same
 * order, same alpha, same blend mode, same mask handling — so a merged layer
 * looks identical to the layers it replaced.
 *
 * "Reproduces" is the honest word: the engine bakes masks its own way for the
 * live canvas. Both go through the same pure maths in `core/model/mask.ts`,
 * which is what keeps the two from drifting.
 */
export async function rasterizeRegion(
  sources: RasterSource[],
  region: Rect,
  options: { background?: string } = {},
): Promise<string> {
  // One preload pass over the whole source tree, deduplicated: a composite
  // group's children and a clip run's members share the same decodes.
  const leaves: LayerSource[] = [];
  collectLeaves(sources, leaves);
  const images = new Map<string, HTMLImageElement>();
  await Promise.all(
    leaves.map(async (source) => {
      if (!source.src || images.has(source.src)) return;
      images.set(source.src, await loadImageUrl(source.src));
    }),
  );
  // Only decode the masks that are actually in force, and only invert the ones
  // that need it.
  const masks = new Map<string, HTMLCanvasElement>();
  await Promise.all(
    leaves.map(async (source) => {
      if (!source.mask || masks.has(source.mask.src)) return;
      masks.set(source.mask.src, await coverageCanvas(source.mask.src, source.mask.inverted));
    }),
  );

  return drawToDataUrl(region.width, region.height, (ctx) => {
    const background = options.background;
    if (background && background !== 'transparent') {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, region.width, region.height);
    }

    for (const source of sources) {
      drawSource(ctx, source, region, images, masks);
    }
  });
}

/**
 * A canvas holding the coverage to composite a mask with.
 *
 * Un-inverted that is the mask itself; inverted it is "everything the mask is
 * not", which is one `destination-out` away from a full-coverage sheet.
 */
async function coverageCanvas(maskSrc: string, inverted: boolean): Promise<HTMLCanvasElement> {
  const image = await loadImageUrl(maskSrc);
  const width = Math.max(1, image.naturalWidth);
  const height = Math.max(1, image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot render a canvas.');
  if (!inverted) {
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
  }
  const pixels = ctx.getImageData(0, 0, width, height);
  const flipped = invertMaskPixels({ data: pixels.data, width, height });
  pixels.data.set(flipped.data);
  ctx.clearRect(0, 0, width, height);
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}
